<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConfiguratorApi } from '@/composables/api/useConfiguratorApi'
import type { PhoneRoutingConfig } from '@/composables/api/useConfiguratorApi'
import { useAlerts } from '@/composables/alert/useAlerts'
import { usePhoneFormatter } from '@/composables/formatting/usePhoneFormatter'
import { alertEnum } from '@/enums/AlertEnum'

const { t } = useI18n()
const { getRoutingConfigs, getConfigurationNames, updateRoutingConfig } = useConfiguratorApi()
const { addAlert } = useAlerts()
const { formatPhone } = usePhoneFormatter()

const emit = defineEmits<{ assigned: [] }>()

interface PhoneItem extends PhoneRoutingConfig {
  phone: string
  hasDefaultError: boolean
  accordionState: 'expanded' | 'collapsed'
  initialAllowed: string[]
  initialDefault: string | null
}

const panelOpen = ref(false)
const isLoading = ref(false)
const withConfig = ref<PhoneItem[]>([])
const withoutConfig = ref<PhoneItem[]>([])
const vaOptions = ref<{ value: string; label: string }[]>([])

const groups = computed(() =>
  [
    { items: withConfig.value, labelKey: 'phone_numbers_with_configuration' },
    { items: withoutConfig.value, labelKey: 'phone_numbers_without_configuration' },
  ].filter((g) => g.items.length > 0),
)

const openPanel = async () => {
  try {
    const [routingConfigs, configNames] = await Promise.all([
      getRoutingConfigs(),
      getConfigurationNames(),
    ])

    vaOptions.value = Object.entries(configNames).map(([id, name]) => ({
      value: id,
      label: name,
    }))

    const validConfigIds = new Set(Object.keys(configNames))
    const withArr: PhoneItem[] = []
    const withoutArr: PhoneItem[] = []

    for (const [phone, config] of Object.entries(routingConfigs)) {
      const allowedConfigIds = config.allowed_config_ids.filter((id) => validConfigIds.has(id))
      const item: PhoneItem = {
        phone,
        allowed_config_ids: allowedConfigIds,
        default_config_id: config.default_config_id,
        hasDefaultError: false,
        accordionState: 'collapsed',
        initialAllowed: [...allowedConfigIds],
        initialDefault: config.default_config_id,
      }
      if (config.default_config_id !== null || allowedConfigIds.length > 0) {
        withArr.push(item)
      } else {
        withoutArr.push(item)
      }
    }

    withConfig.value = withArr
    withoutConfig.value = withoutArr
    panelOpen.value = true
  } catch {
    addAlert('error_occurred_try_again', alertEnum.Danger)
  }
}

const allItems = computed(() => [...withConfig.value, ...withoutConfig.value])

type DropdownPrimitive = boolean | string | number
type DropdownModel = DropdownPrimitive | DropdownPrimitive[] | null | undefined

const onAllowedChange = (item: PhoneItem, value: DropdownModel) => {
  const newValue = Array.isArray(value) ? (value as string[]) : []
  item.allowed_config_ids = newValue
  if (item.default_config_id && !newValue.includes(item.default_config_id)) {
    item.default_config_id = null
  }
  if (newValue.length === 0 || (newValue.length > 0 && item.default_config_id)) {
    item.hasDefaultError = false
  }
}

const onDefaultChange = (item: PhoneItem, value: DropdownModel) => {
  const newValue = typeof value === 'string' ? value : null
  item.default_config_id = newValue
  if (newValue) {
    item.hasDefaultError = false
  }
}

const defaultOptionsFor = (item: PhoneItem) =>
  vaOptions.value.filter((opt) => item.allowed_config_ids.includes(opt.value))

const save = async () => {
  let hasErrors = false
  for (const item of allItems.value) {
    if (item.allowed_config_ids.length > 0 && !item.default_config_id) {
      item.hasDefaultError = true
      item.accordionState = 'expanded'
      hasErrors = true
    }
  }
  if (hasErrors) return

  const changed = allItems.value.filter((item) => {
    const allowedChanged =
      JSON.stringify([...item.allowed_config_ids].sort()) !==
      JSON.stringify([...item.initialAllowed].sort())
    const defaultChanged = item.default_config_id !== item.initialDefault
    return allowedChanged || defaultChanged
  })

  if (changed.length === 0) {
    panelOpen.value = false
    return
  }

  isLoading.value = true
  try {
    const results = await Promise.all(
      changed.map((item) =>
        updateRoutingConfig(item.phone, item.default_config_id, item.allowed_config_ids),
      ),
    )

    // Update initial snapshot for items that succeeded so a retry doesn't re-send them
    for (let i = 0; i < results.length; i++) {
      if (results[i]) {
        changed[i].initialAllowed = [...changed[i].allowed_config_ids]
        changed[i].initialDefault = changed[i].default_config_id
      }
    }

    if (results.some((result) => !result)) {
      addAlert('error_occurred_try_again', alertEnum.Danger)
      return
    }
    addAlert('phone_numbers_assigned_successfully', alertEnum.Success)
    emit('assigned')
    panelOpen.value = false
  } finally {
    isLoading.value = false
  }
}

defineExpose({ open: openPanel })
</script>

<template>
  <ORightPanel v-model:open="panelOpen" :title="t('assign_phone_number')">
    <div class="flex flex-col gap-l">
      <template v-for="(group, gi) in groups" :key="group.labelKey">
        <ODivider v-if="gi > 0" />
        <div class="flex flex-col gap-l">
          <div class="typography-body-1-medium text-neutral-1">
            {{ t(group.labelKey, [group.items.length]) }}
          </div>
          <OAccordion
            v-for="item in group.items"
            :key="item.phone"
            variant="card"
            :title="formatPhone(item.phone)"
            :use-default-content-wrapper="false"
            v-model:state="item.accordionState"
          >
            <div class="flex flex-col gap-l p-l">
              <ODropdown
                :model-value="item.allowed_config_ids"
                :options="vaOptions"
                :label="t('configurations_on_this_number')"
                multi-select
                medium
                teleport
                searchable
                :placeholder="t('no_agent_assigned')"
                @update:model-value="onAllowedChange(item, $event)"
              />
              <ODropdown
                :model-value="item.default_config_id"
                :options="defaultOptionsFor(item)"
                :label="t('default_configuration')"
                medium
                teleport
                searchable
                :disabled="item.allowed_config_ids.length === 0"
                :placeholder="t('select_a_configuration')"
                :error="item.hasDefaultError"
                :error-message="item.hasDefaultError ? t('default_configuration_required') : ''"
                @update:model-value="onDefaultChange(item, $event)"
              />
            </div>
          </OAccordion>
        </div>
      </template>
    </div>
    <template #footer>
      <OButton
        variant="secondary"
        color="neutral"
        :label="t('cancel')"
        size="L"
        @mousedown.prevent
        @click="panelOpen = false"
      />
      <OButton
        data-testid="save-btn"
        :label="t('save')"
        size="L"
        :loading="isLoading"
        backgroundColor="success"
        @click="save"
      />
    </template>
  </ORightPanel>
</template>
